import { useEffect, useMemo, useRef, useState } from 'react';

export interface MultiSelectOption {
  value: string;
  label: string;
}

interface Props {
  options: MultiSelectOption[];
  selected: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  /** Strip a leading character from typed input before matching (e.g. '@'). */
  trimLeadingChar?: string;
  /**
   * Allow adding values that aren't in [options]. The typed query becomes the value/label.
   * Used for branch names — past branches come from the API, new ones from free-text entry.
   */
  allowFreeText?: boolean;
}

export default function MultiSelect({ options, selected, onChange, placeholder, trimLeadingChar, allowFreeText }: Props) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const normalizedQuery = useMemo(() => {
    let q = query.trim().toLowerCase();
    if (trimLeadingChar && q.startsWith(trimLeadingChar)) q = q.slice(trimLeadingChar.length);
    return q;
  }, [query, trimLeadingChar]);

  const filtered = useMemo(() => {
    const selectedSet = new Set(selected);
    return options
      .filter(o => !selectedSet.has(o.value))
      .filter(o => !normalizedQuery || o.label.toLowerCase().includes(normalizedQuery));
  }, [options, selected, normalizedQuery]);

  useEffect(() => {
    setActiveIdx(0);
  }, [normalizedQuery, open]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const selectedOptions = useMemo(() => {
    const map = new Map(options.map(o => [o.value, o]));
    return selected.map(v => map.get(v) ?? { value: v, label: v });
  }, [selected, options]);

  const add = (value: string) => {
    if (!selected.includes(value)) onChange([...selected, value]);
    setQuery('');
    inputRef.current?.focus();
  };

  const remove = (value: string) => {
    onChange(selected.filter(v => v !== value));
    inputRef.current?.focus();
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setActiveIdx(i => Math.min(i + 1, Math.max(filtered.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const opt = filtered[activeIdx];
      if (opt) {
        add(opt.value);
      } else if (allowFreeText && normalizedQuery && !selected.includes(normalizedQuery)) {
        add(normalizedQuery);
      }
    } else if (e.key === 'Backspace' && query === '' && selected.length > 0) {
      remove(selected[selected.length - 1]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div className="multiselect" ref={rootRef}>
      <div className="multiselect-control" onClick={() => inputRef.current?.focus()}>
        {selectedOptions.map(opt => (
          <span key={opt.value} className="multiselect-chip">
            {opt.label}
            <button
              type="button"
              className="multiselect-chip-remove"
              aria-label={`Remove ${opt.label}`}
              onClick={e => {
                e.stopPropagation();
                remove(opt.value);
              }}
            >
              ×
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          className="multiselect-input"
          value={query}
          placeholder={selectedOptions.length === 0 ? placeholder : ''}
          onChange={e => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
        />
      </div>
      {open ? (
        <div className="multiselect-dropdown" role="listbox">
          {filtered.length === 0 ? (
            allowFreeText && normalizedQuery ? (
              <div
                className="multiselect-option is-active"
                role="option"
                aria-selected
                onMouseDown={e => {
                  e.preventDefault();
                  add(normalizedQuery);
                }}
              >
                Add "{normalizedQuery}"
              </div>
            ) : (
              <div className="multiselect-empty">No matches</div>
            )
          ) : (
            filtered.map((opt, idx) => (
              <div
                key={opt.value}
                role="option"
                aria-selected={idx === activeIdx}
                className={`multiselect-option${idx === activeIdx ? ' is-active' : ''}`}
                onMouseEnter={() => setActiveIdx(idx)}
                onMouseDown={e => {
                  e.preventDefault();
                  add(opt.value);
                }}
              >
                {opt.label}
              </div>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}
